import { Box, FormControl, FormHelperText, Typography } from '@mui/material'
import { ReactElement, useContext } from 'react'
import { useDropzone } from 'react-dropzone'
import { useField } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  DROPZONE_ERRORS,
  EMAIL_FILE_EXTENSIONS,
  EMAIL_FILE_MIME_ACCEPT_TYPES,
  FILE_EXTENSIONS,
  FILE_MAX_SIZE_IN_BYTES,
  FILE_MAX_SIZE_IN_MB,
  FILE_MIME_ACCEPT_TYPES,
  FILE_MIN_SIZE_IN_BYTES,
} from 'src/shared/constants/file-constants'
import { CloudUploadIcon, DocumentIcon } from 'src/shared/icons/Icons'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import styled, { CSSProperties, DefaultTheme, ThemeContext } from 'styled-components'

type FileUploadFieldProps = {
  validate?: any
  name: string
  acceptEmailTypes?: boolean
  multiple?: boolean
  maxFiles?: number
  helperText?: string
  onFilesChange?: (newFilesAdded: File[]) => void
}

const GetBorderColor = ({ $isDragReject, $isDragAccept }: Props): CSSProperties['border'] => {
  const themeContext = useContext(ThemeContext as React.Context<DefaultTheme>)

  if ($isDragReject) {
    return themeContext.colors.error.main
  }
  if ($isDragAccept) {
    return themeContext.colors.success.main
  }
  return themeContext.colors.grey.divider
}

const GetBackgroundColor = ({ $isDragReject, $isDragAccept, error }: Props): CSSProperties['backgroundColor'] => {
  const themeContext = useContext(ThemeContext as React.Context<DefaultTheme>)
  if ($isDragAccept) {
    return themeContext.colors.success.light
  }
  if ($isDragReject || error) {
    return themeContext.colors.error.light
  }

  return themeContext.colors.grey.neutral
}

interface Props {
  $isDragAccept: boolean
  $isDragActive: boolean
  $isDragReject: boolean
  error?: boolean
}

const DropzoneWrapper = styled(FormControl)<Props>`
  height: 16rem;
  border: 2px dashed ${(props) => GetBorderColor(props)};
  display: flex;
  grid-gap: ${({ theme }) => theme.spacing(1)};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${({ theme }) => theme.spacing(2)};
  background-color: ${(props) => GetBackgroundColor(props)};
  font-weight: ${({ theme }) => theme.typography.h6.fontWeight};
  transition: ${({ theme }) =>
    theme.transitions.create('border', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
    })};

  &:hover {
    cursor: pointer;
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary.dark};
    background-color: ${({ theme }) => theme.colors.action.focus};
  }
`

const CloudUploadIconStyled = styled(CloudUploadIcon)`
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.text.disabled};
`

const TypographyStyled = styled(Typography)`
  color: ${({ theme }) => theme.colors.text.disabled};
`

const DocumentIconStyled = styled(DocumentIcon)`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 2.5rem;
  margin: 0.25rem 0;
`

const FileListContainer = styled.div`
  max-height: 150px;
  overflow-y: auto;
  width: 100%;
`

const FileNameTypographyStyled = styled(Typography)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  font-weight: inherit;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const FileUploadField = (props: FileUploadFieldProps): ReactElement => {
  const { name, validate, acceptEmailTypes, multiple = false, maxFiles } = props
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()

  const {
    input: { onChange, onFocus, onBlur, value },
    meta: { touched, error: errorObject },
  } = useField(name, { validate })

  const error = errorObject && getMessage(errorObject.errorKey, errorObject.params)
  const invalid = Boolean(touched && error)

  // Normalize value based on mode
  const files: File[] = multiple ? (Array.isArray(value) ? value : []) : value?.name ? [value] : []

  const ACCEPT_TYPES = {
    ...FILE_MIME_ACCEPT_TYPES,
    ...(acceptEmailTypes && EMAIL_FILE_MIME_ACCEPT_TYPES),
  }

  const ACCEPT_FILE_EXTENSIONS = acceptEmailTypes ? `${FILE_EXTENSIONS},${EMAIL_FILE_EXTENSIONS}` : `${FILE_EXTENSIONS}`

  const { getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject } = useDropzone({
    multiple: multiple,
    maxFiles: multiple ? maxFiles : undefined,
    noDrag: false,
    maxSize: FILE_MAX_SIZE_IN_BYTES,
    minSize: FILE_MIN_SIZE_IN_BYTES,
    accept: ACCEPT_TYPES,
    onDropAccepted: (acceptedFiles) => {
      if (multiple) {
        // Append new files to existing files
        onChange([...files, ...acceptedFiles])
      } else {
        onChange(acceptedFiles[0])
      }

      // Trigger the custom callback if provided
      if (props.onFilesChange) {
        props.onFilesChange(acceptedFiles)
      }
    },
    onDrop: (acceptedFiles, rejectedFiles) => {
      validate && onBlur() // Trigger validation on drop

      const fileErrors = rejectedFiles.map((r) => r.errors).flat()
      const hasTooManyFiles = fileErrors.some((r) => r.code === DROPZONE_ERRORS.TOO_MANY_FILES)

      if (multiple) {
        // multiple file upload validation messages

        // 1. All rejected due to "Too Many Files"
        if (hasTooManyFiles) {
          notificationService.error(
            getMessage('validation.multiple-file.too.many.files', [maxFiles ? maxFiles.toString() : '']),
          )
        } else {
          // 2. Specific file errors
          const invalidTypeFiles = rejectedFiles
            .filter((r) => r.errors.some((e) => e.code === DROPZONE_ERRORS.FILE_INVALID_TYPE))
            .map((r) => r.file.name)

          if (invalidTypeFiles.length > 0) {
            notificationService.error(
              getMessage('validation.multiple-file.invalid.type', [
                ACCEPT_FILE_EXTENSIONS,
                invalidTypeFiles.join(', '),
              ]),
            )
          }

          const tooLargeFiles = rejectedFiles
            .filter((r) => r.errors.some((e) => e.code === DROPZONE_ERRORS.FILE_TOO_LARGE))
            .map((r) => r.file.name)

          if (tooLargeFiles.length > 0) {
            notificationService.error(
              getMessage('validation.multiple-file.max.size', [
                FILE_MAX_SIZE_IN_MB.toFixed(2),
                tooLargeFiles.join(', '),
              ]),
            )
          }

          const tooSmallFiles = rejectedFiles
            .filter((r) => r.errors.some((e) => e.code === DROPZONE_ERRORS.FILE_TOO_SMALL))
            .map((r) => r.file.name)

          if (tooSmallFiles.length > 0) {
            notificationService.error(getMessage('validation.multiple-file.min.size', [tooSmallFiles.join(', ')]))
          }
        }
      } else {
        // single file upload validation messages

        const hasAcceptedFile = acceptedFiles.length > 0
        const hasAnyError = fileErrors.length > 0

        if (hasAcceptedFile && hasAnyError) {
          notificationService.info(getMessage('label.file.too.many.files.info'))
        } else if (hasTooManyFiles) {
          notificationService.error(getMessage('validation.file.too.many.files'))
        } else if (!hasAcceptedFile) {
          const firstErrorCode = fileErrors.filter((r) => r.code !== DROPZONE_ERRORS.TOO_MANY_FILES)?.[0]?.code
          if (firstErrorCode === DROPZONE_ERRORS.FILE_INVALID_TYPE) {
            notificationService.error(getMessage('validation.file.invalid.type', [ACCEPT_FILE_EXTENSIONS]))
          } else if (firstErrorCode === DROPZONE_ERRORS.FILE_TOO_LARGE) {
            notificationService.error(getMessage('validation.file.max.size', [FILE_MAX_SIZE_IN_MB.toFixed(2)]))
          } else if (firstErrorCode === DROPZONE_ERRORS.FILE_TOO_SMALL) {
            notificationService.error(getMessage('validation.file.min.size'))
          }
        }
      }
    },
  })

  return (
    <Box>
      <DropzoneWrapper
        {...getRootProps({ color: undefined })}
        $isDragAccept={isDragAccept}
        $isDragActive={isDragActive}
        $isDragReject={isDragReject}
        error={invalid}
      >
        <input {...getInputProps()} onFocus={onFocus} onBlur={onBlur} />
        {files.length === 0 && (
          <>
            <CloudUploadIconStyled />
            <TypographyStyled>
              {multiple
                ? getMessage('label.multiple-file.upload.modal.dropzone.placeholder')
                : getMessage('label.file.upload.modal.dropzone.placeholder')}
            </TypographyStyled>
            {props.helperText && (
              <Typography variant="caption" color="textSecondary" sx={{ marginTop: 0.5 }}>
                {props.helperText}
              </Typography>
            )}
          </>
        )}

        {files.length > 0 && (
          <>
            <DocumentIconStyled />
            <FileListContainer>
              {files.slice(0, 2).map((file, index) => (
                <FileNameTypographyStyled key={`${file.name}-${index}`}>{file.name}</FileNameTypographyStyled>
              ))}

              {files.length > 2 && <FileNameTypographyStyled>...</FileNameTypographyStyled>}
            </FileListContainer>
          </>
        )}
      </DropzoneWrapper>
      <FormHelperText error>{(invalid && error) || ' '}</FormHelperText>
    </Box>
  )
}
